import React, { useRef, useCallback, useMemo, useState, useEffect } from "react";
import ChipGroup from "../../../components/select/ChipGroup";
import ChipButton from "../../../components/select/ChipButton";
import RangeSlider from "../../../components/input/RangeSlider";
import RangeInputPair from "../../../components/input/RangeInputPair";
import UnitToggle from "../../../components/select/UnitToggle";
import HorizontalScrollContainer from "./HorizontalScrollContainer";
import styles from "./FullFilter.module.scss";

/**
 * 전체 필터 컴포넌트
 *
 * 피그마 구조:
 * - 상단: "전체 필터" 제목 + X 닫기
 * - 앵커탭: 물건유형 | 거래유형 | 가격 | 면적 | 용도지역
 * - 본문: 섹션별 필터 렌더링
 * - 하단: 초기화 + "N개 매물 보기"
 */

// ── 카테고리 탭 필터 (물건유형 전용) ──
// 1단: 전체 / 상가・사무실・공장 / 아파트・오피스텔 / ... (탭)
// 2단: 선택된 카테고리의 subOptions (칩)
const CategoryTabFilter = ({ categories, value, onChange, selectedCategoryValue, onCategoryChange }) => {
  const selectedCategoryIdx = selectedCategoryValue != null
    ? categories.findIndex((cat) => cat.value === selectedCategoryValue)
    : -1;

  const selectedCategory =
    selectedCategoryIdx >= 0 ? categories[selectedCategoryIdx] : null;

  // randType은 평탄화된 쉼표 구분 문자열 (예: "10,11,12,15,17,18,19")
  const valueArray = useMemo(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) return [];
    if (typeof value === "string") return value.split(",").map((v) => v.trim()).filter(Boolean);
    return Array.isArray(value) ? value.map(String) : [];
  }, [value]);

  // subOption value가 "10,11" 같은 복합값일 수 있으므로 — 구성 타입이 모두 포함된 경우 선택됨
  const selectedSubOptionValues = useMemo(() => {
    if (!selectedCategory) return [];
    return selectedCategory.subOptions
      .filter((opt) => {
        const parts = String(opt.value).split(",").map((s) => s.trim());
        return parts.length > 0 && parts.every((p) => valueArray.includes(p));
      })
      .map((opt) => opt.value);
  }, [selectedCategory, valueArray]);

  // 카테고리 탭 클릭 — 탭 전환 + 해당 카테고리 서브항목 전체 자동 선택
  const handleCategoryClick = (idx) => {
    const newIdx = selectedCategoryIdx === idx ? -1 : idx;
    const categoryValue = newIdx >= 0 ? categories[newIdx].value : null;
    onCategoryChange(categoryValue);
    if (newIdx >= 0) {
      onChange(categories[newIdx].subOptions.map((o) => o.value));
    } else {
      onChange([]);
    }
  };

  return (
    <div className={styles.categoryTabFilter}>
      {/* 카테고리 탭 바 */}
      <HorizontalScrollContainer className={styles.categoryTabs}>
        <button
          type="button"
          className={`${styles.categoryTab} ${selectedCategoryIdx === -1 ? styles.categoryTabActive : ""}`}
          onClick={() => handleCategoryClick(-1)}
        >
          전체
        </button>
        {categories.map((cat, idx) => (
          <button
            key={cat.value}
            type="button"
            className={`${styles.categoryTab} ${selectedCategoryIdx === idx ? styles.categoryTabActive : ""}`}
            onClick={() => handleCategoryClick(idx)}
          >
            {cat.label}
          </button>
        ))}
      </HorizontalScrollContainer>

      {/* 하위 칩 — 피그마: pt 16, px 20, gap 8, h 36 */}
      <div className={styles.categoryChips}>
        {selectedCategory ? (
          <ChipGroup
            options={selectedCategory.subOptions}
            value={
              // 카테고리 내 전체 선택 시 "전체" 버튼 active 표시를 위해 빈 배열로 변환 (서브옵션 2개 이상일 때만)
              selectedCategory.subOptions.length > 1 && selectedSubOptionValues.length >= selectedCategory.subOptions.length ? [] : selectedSubOptionValues
            }
            onChange={(val) => {
              // ChipGroup이 [] 반환 = "전체" 클릭 → 카테고리 내 전체 서브항목 선택
              if (Array.isArray(val) && val.length === 0) {
                onChange(selectedCategory.subOptions.map((o) => o.value));
              } else {
                onChange(val);
              }
            }}
            multiple
            showAll={selectedCategory.subOptions.length > 1}
            size="md"
          />
        ) : (
          <ChipGroup
            options={[{ value: "__all__", label: "전체유형" }]}
            value={["__all__"]}
            onChange={() => {}}
            multiple={false}
            size="md"
          />
        )}
      </div>
    </div>
  );
};

// ── 필터 블록 렌더링 ──

const FilterSection = ({ section, filter, onChange, connectedAbove, connectedBelow }) => {
  return (
    <div
      className={styles.section}
      data-section-id={section.id}
      data-connected-above={connectedAbove || undefined}
      data-connected-below={connectedBelow || undefined}
      data-section-py={section.sectionPy || undefined}
    >
      {section.filters.map((f, idx) => {
        const visible =
          f.visible == null
            ? true
            : typeof f.visible === "function"
              ? f.visible(filter)
              : f.visible;

        if (!visible) return null;

        // range + rangeTitle 있으면 섹션 라벨(blue), 없으면 가격 라벨(black 400)
        const isRangeLabel = f.type === "range" && !f.rangeTitle;
        const labelClass = isRangeLabel
          ? `${styles.filterLabel} ${styles.filterLabelRange}`
          : styles.filterLabel;
        const headerClass = isRangeLabel
          ? `${styles.filterHeader} ${styles.filterHeaderRange}`
          : styles.filterHeader;

        return (
          <React.Fragment key={f.id}>
            {/* 피그마: range↔range 간 0.5px thin divider (매매↔보증금↔월세) */}
            {f.type === "range" && (() => {
              // 이전에 실제로 보이는 range 필터가 있을 때만 divider 표시
              const prevVisible = section.filters.slice(0, idx).reverse().find((pf) => {
                const pv = pf.visible == null ? true : typeof pf.visible === "function" ? pf.visible(filter) : pf.visible;
                return pv;
              });
              return prevVisible?.type === "range";
            })() && (
              <div className={styles.thinDivider} />
            )}
            <div className={styles.filterBlock}>
              {f.label && (
                <div className={headerClass}>
                  <span className={labelClass}>
                    {f.label}
                    {f.sublabel && (
                      <span className={styles.filterSublabel}> {f.sublabel}</span>
                    )}
                  </span>
                  {f.headerRight}
                </div>
              )}
              {renderFilter(f, filter, onChange)}
              {f.checkbox && (
                <div className={styles.checkboxRow}>
                  <button
                    type="button"
                    className={styles.checkbox}
                    data-checked={!!filter[f.checkbox.key]}
                    onClick={() => onChange(f.checkbox.key, !filter[f.checkbox.key])}
                  >
                    <span className={styles.checkboxIcon} data-checked={!!filter[f.checkbox.key]} />
                    <span className={`${styles.checkboxLabel} ${filter[f.checkbox.key] ? styles.checkboxLabelActive : ""}`}>{f.checkbox.label}</span>
                  </button>
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

function renderFilter(f, filter, onChange) {
  switch (f.type) {
    case "category-tab":
      return (
        <CategoryTabFilter
          categories={f.categories}
          value={filter[f.id]}
          onChange={(val) => onChange(f.id, val)}
          selectedCategoryValue={filter.selectedCategory}
          onCategoryChange={(val) => onChange("selectedCategory", val)}
        />
      );

    case "chip":
      return (
        <div className={styles.chipArea}>
          <ChipGroup
            options={f.options}
            value={filter[f.id]}
            onChange={(val) => onChange(f.id, val)}
            multiple={f.multiple}
            showAll={f.showAll}
            size={f.chipSize}
          />
        </div>
      );

    case "grouped-chip": {
      const value = filter[f.id] || [];
      const allEmpty = value.length === 0;

      return (
        <div className={styles.groupedChips}>
          {/* 최상단 "전체" */}
          <div className={styles.groupedChipTopSection}>
            <div className={styles.groupedChipArea}>
              <ChipButton
                label="전체"
                selected={allEmpty}
                size="md"
                onClick={() => onChange(f.id, [])}
              />
            </div>
            <div className={styles.groupedChipDivider} />
          </div>

          {/* 그룹별 */}
          {f.groups.map((group) => {
            const groupVals = group.options.map((o) => String(o.value));
            const groupSelected = value.filter((v) => groupVals.includes(String(v)));
            // "전체" active = 그룹 항목 전부 명시적으로 선택됨 (미선택과 구분)
            const allGroupSelected = group.options.length > 0 && groupSelected.length === group.options.length;

            const handleGroupAllClick = () => {
              const withoutGroup = value.filter((v) => !groupVals.includes(String(v)));
              onChange(f.id, [...withoutGroup, ...group.options.map((o) => o.value)]);
            };

            const handleOptionClick = (optValue) => {
              const withoutGroup = value.filter((v) => !groupVals.includes(String(v)));
              if (allGroupSelected) {
                // 전체 상태에서 개별 클릭 → 그 항목만 선택
                onChange(f.id, [...withoutGroup, optValue]);
              } else {
                const isSelected = groupSelected.some((v) => String(v) === String(optValue));
                const next = isSelected
                  ? groupSelected.filter((v) => String(v) !== String(optValue))
                  : [...groupSelected, optValue];
                // 모든 항목 선택 시 전체 상태로 전환
                if (next.length >= group.options.length) {
                  onChange(f.id, [...withoutGroup, ...group.options.map((o) => o.value)]);
                } else {
                  onChange(f.id, [...withoutGroup, ...next]);
                }
              }
            };

            return (
              <div key={group.label} className={styles.groupedChipGroup}>
                <div className={styles.groupedChipGroupLabel}>{group.label}</div>
                <div className={styles.groupedChipGroupBody}>
                  <div className={styles.groupedChipArea}>
                    <div className={styles.groupedChipChips}>
                      <ChipButton
                        label="전체"
                        selected={allGroupSelected}
                        size={f.chipSize || "md"}
                        onClick={handleGroupAllClick}
                      />
                      {group.options.map((opt) => (
                        <ChipButton
                          key={opt.value}
                          label={opt.label}
                          selected={!allGroupSelected && groupSelected.some((v) => String(v) === String(opt.value))}
                          size={f.chipSize || "md"}
                          onClick={() => handleOptionClick(opt.value)}
                          disabled={opt.disabled}
                        />
                      ))}
                    </div>
                  </div>
                  <div className={styles.groupedChipDivider} />
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    case "range": {
      const unitToggleOptions = f.unitToggle;
      const unitIndex = unitToggleOptions
        ? unitToggleOptions.findIndex((u) => u.value === filter[f.unitKey])
        : -1;
      const activeUnit = unitToggleOptions
        ? unitToggleOptions[Math.max(0, unitIndex)]
        : null;

      const rangeConfig = activeUnit?.rangeConfig || f;
      const unit = activeUnit?.label || f.unit;
      const format = activeUnit?.format || f.format;
      const parse = activeUnit?.parse || f.parse;

      return (
        <div className={styles.rangeBlock}>
          {(f.rangeTitle || unitToggleOptions) && (
            <div className={styles.rangeHeader}>
              {f.rangeTitle && <span className={styles.rangeTitle}>{f.rangeTitle}</span>}
              {unitToggleOptions && (
                <UnitToggle
                  options={unitToggleOptions}
                  activeIndex={Math.max(0, unitIndex)}
                  onChange={(idx) => onChange(f.unitKey, unitToggleOptions[idx].value)}
                />
              )}
            </div>
          )}
          <RangeInputPair
            minValue={filter[f.minKey || `min${capitalize(f.id)}`]}
            maxValue={filter[f.maxKey || `max${capitalize(f.id)}`]}
            onMinChange={(val) => onChange(f.minKey || `min${capitalize(f.id)}`, val)}
            onMaxChange={(val) => onChange(f.maxKey || `max${capitalize(f.id)}`, val)}
            unit={unit}
            format={format}
            parse={parse}
            convertLabel={f.convertLabel}
            minPlaceholder={f.minPlaceholder || "최소"}
            maxPlaceholder={f.maxPlaceholder || "최대"}
          />
          <RangeSlider
            min={rangeConfig.min}
            max={rangeConfig.max}
            step={rangeConfig.step}
            values={[
              filter[f.minKey || `min${capitalize(f.id)}`],
              filter[f.maxKey || `max${capitalize(f.id)}`],
            ]}
            onFinalChange={(vals) => {
              onChange(f.minKey || `min${capitalize(f.id)}`, vals[0]);
              onChange(f.maxKey || `max${capitalize(f.id)}`, vals[1]);
            }}
            minLabel={f.minLabel || "최소"}
            maxLabel={f.maxLabel || "최대"}
          />
        </div>
      );
    }

    // 피그마: 36px 높이, rounded-10px, 라디오 아이콘 + 텍스트, 단일 선택
    case "radio": {
      const currentValue = filter[f.id];
      const isAllSelected = currentValue == null;

      return (
        <div className={styles.radioGroup}>
          {f.showAll && (
            <button
              type="button"
              className={`${styles.radioChip} ${isAllSelected ? styles.radioChipActive : ""}`}
              onClick={() => onChange(f.id, null)}
            >
              <span className={styles.radioIcon} data-checked={isAllSelected} />
              <span>전체</span>
            </button>
          )}
          {f.options.map((opt) => {
            const isSelected = String(currentValue) === String(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                className={`${styles.radioChip} ${isSelected ? styles.radioChipActive : ""}`}
                onClick={() => onChange(f.id, isSelected ? null : opt.value)}
              >
                <span className={styles.radioIcon} data-checked={isSelected} />
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>
      );
    }

    // 피그마: "전체" 버튼(40px) + divider + 서브그룹(방수/욕실수)별 label + 칩
    case "interior-group": {
      // 모든 그룹의 값이 비어있으면 "전체" 선택 상태
      const roomValue = filter["roomCnt"] || [];
      const bathroomValue = filter["bathroomCnt"] || [];
      const allEmpty =
        (!roomValue || (Array.isArray(roomValue) && roomValue.length === 0)) &&
        (!bathroomValue || (Array.isArray(bathroomValue) && bathroomValue.length === 0));

      return (
        <div className={styles.interiorGroup}>
          {/* 전체 버튼 */}
          <div className={styles.interiorAllRow}>
            <button
              type="button"
              className={styles.interiorAllBtn}
              data-selected={allEmpty}
              onClick={() => {
                onChange("roomCnt", []);
                onChange("bathroomCnt", []);
              }}
            >
              전체
            </button>
          </div>

          {f.groups.map((group, idx) => {
            const key = group.label === "방수" ? "roomCnt" : "bathroomCnt";
            const value = group.label === "방수" ? roomValue : bathroomValue;

            return (
              <React.Fragment key={group.label}>
                {/* 피그마: 전체↔방수, 방수↔욕실수 모두 1px divider */}
                <div className={styles.interiorDivider} />
                <div className={styles.interiorSubgroup}>
                  <div className={styles.interiorSubLabel}>{group.label}</div>
                  <div className={styles.interiorChips}>
                    <ChipGroup
                      options={group.options}
                      value={value}
                      onChange={(val) => onChange(key, val)}
                      multiple
                      showAll
                      size="md"
                    />
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      );
    }

    default:
      return null;
  }
}

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// ── 메인 컴포넌트 ──

const FullFilter = ({
  sections,
  filter,
  onChange,
  onReset,
  onApply,
  onClose,
  resultCount,
  className,
}) => {
  const contentRef = useRef(null);
  const containerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const [activeSection, setActiveSection] = useState(null);

  // 모달 내부에서 mousedown → mouseup까지 드래그 상태 추적
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onDown = () => { isDraggingRef.current = true; };
    const onUp = () => { setTimeout(() => { isDraggingRef.current = false; }, 0); };
    container.addEventListener("mousedown", onDown);
    document.addEventListener("mouseup", onUp);
    return () => {
      container.removeEventListener("mousedown", onDown);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  // 외부 클릭 시 닫기 (드래그 중이면 무시)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isDraggingRef.current) return;
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onClose();
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside, true);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [onClose]);

  const sectionNames = useMemo(
    () => sections.map((s) => ({ id: s.id, label: s.section })),
    [sections]
  );

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const updateActiveSection = () => {
      const sectionEls = container.querySelectorAll("[data-section-id]");
      const scrollTop = container.scrollTop;

      // 스크롤이 하단에 도달하면 마지막 섹션 활성화
      const atBottom = scrollTop + container.clientHeight >= container.scrollHeight - 2;
      if (atBottom && sectionEls.length > 0) {
        setActiveSection(sectionEls[sectionEls.length - 1].dataset.sectionId);
        return;
      }

      let active = null;
      for (const el of sectionEls) {
        const elTop = el.offsetTop - container.offsetTop;
        if (elTop <= scrollTop + 40) {
          active = el.dataset.sectionId;
        }
      }

      if (active) {
        setActiveSection(active);
      } else if (sections.length > 0) {
        setActiveSection(sections[0].id);
      }
    };

    container.addEventListener("scroll", updateActiveSection, { passive: true });
    updateActiveSection();

    return () => container.removeEventListener("scroll", updateActiveSection);
  }, [sections]);

  const scrollToSection = useCallback((sectionId) => {
    const container = contentRef.current;
    if (!container) return;
    const el = container.querySelector(`[data-section-id="${sectionId}"]`);
    if (el) {
      // container 내부에서만 스크롤 (scrollIntoView는 전체 뷰포트를 움직여 앵커탭이 사라짐)
      const offset = el.offsetTop - container.offsetTop;
      container.scrollTo({ top: offset, behavior: "smooth" });
    }
  }, []);

  return (
    <div className={`${styles.container} ${className || ""}`} ref={containerRef}>
      {/* 피그마: "전체 필터" 제목 + X 닫기 */}
      <div className={styles.titleBar}>
        <span className={styles.titleText}>전체 필터</span>
        <button type="button" className={styles.closeButton} onClick={onClose}>
          <CloseIcon />
        </button>
      </div>

      {/* 앵커 탭 */}
      <HorizontalScrollContainer className={styles.anchorTabs}>
        {sectionNames.map((s) => (
          <button
            key={s.id}
            type="button"
            className={styles.anchorTab}
            data-active={activeSection === s.id}
            onClick={() => scrollToSection(s.id)}
          >
            {s.label}
          </button>
        ))}
      </HorizontalScrollContainer>

      {/* 본문 */}
      <div className={styles.content} ref={contentRef}>
        {sections.map((section, idx) => {
          const nextSection = sections[idx + 1];
          const isLast = idx === sections.length - 1;
          const connectedBelow = !!nextSection?.connectedAbove;

          return (
            <React.Fragment key={section.id}>
              <FilterSection
                section={section}
                filter={filter}
                onChange={onChange}
                connectedAbove={section.connectedAbove}
                connectedBelow={connectedBelow}
              />
              {!isLast && (
                <div className={connectedBelow ? styles.thinDivider : styles.sectionDivider} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* 하단바 */}
      <div className={styles.bottomBar}>
        <button type="button" className={styles.resetButton} onClick={onReset}>
          <ResetIcon />
          <span>초기화</span>
        </button>
        <button type="button" className={styles.applyButton} onClick={onApply}>
          {resultCount != null ? `${resultCount}개 매물 보기` : "매물 보기"}
        </button>
      </div>
    </div>
  );
};

// ── 아이콘 ──

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ResetIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.87972 5.64276L6.78103 4.54407C9.20986 3.1078 12.3926 3.43423 14.4796 5.52127C16.9529 7.99459 16.9529 12.0046 14.4796 14.478C12.0063 16.9513 7.99623 16.9513 5.52291 14.478C4.28592 13.241 3.66773 11.6211 3.66792 9.99888C3.66797 9.58467 3.33222 9.24884 2.918 9.24879C2.50379 9.24875 2.16797 9.5845 2.16792 9.99871C2.16769 12.0026 2.93261 14.009 4.46225 15.5386C7.52136 18.5977 12.4811 18.5977 15.5403 15.5386C18.5994 12.4795 18.5994 7.51971 15.5403 4.46061C12.8636 1.78397 8.73342 1.44982 5.69282 3.45586L4.63881 2.40185C4.42431 2.18735 4.10173 2.12319 3.82147 2.23927C3.54122 2.35536 3.35848 2.62884 3.35848 2.93218L3.35848 6.17309C3.35848 6.372 3.4375 6.56277 3.57815 6.70342C3.71881 6.84407 3.90957 6.92309 4.10848 6.92309H7.34939C7.65274 6.92309 7.92621 6.74036 8.0423 6.4601C8.15838 6.17985 8.09422 5.85726 7.87972 5.64276Z" fill="currentColor" />
  </svg>
);

export default FullFilter;
